/**
 * Realtime helpers para chat.
 *
 * Patrón idéntico a features/tasks/realtime.ts: callback solo dispara
 * invalidaciones de TanStack Query, evitando merge manual.
 */
import { supabase } from '@/lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';

export type ChatRealtimeKind =
  | 'channel'
  | 'member'
  | 'message'
  | 'reaction'
  | 'mention';

export interface ChatRealtimeEvent {
  kind: ChatRealtimeKind;
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  channelId: string | null;
  messageId: string | null;
}

export function subscribeToChat(
  onEvent: (event: ChatRealtimeEvent) => void,
): RealtimeChannel {
  const ch = supabase.channel('chat-realtime');

  const subscribe = (
    table: 'channels' | 'channel_members' | 'messages' | 'message_reactions' | 'mentions',
    kind: ChatRealtimeKind,
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (ch as any).on(
      'postgres_changes',
      { event: '*', schema: 'core', table },
      (payload: {
        eventType: 'INSERT' | 'UPDATE' | 'DELETE';
        new: Record<string, unknown> | null;
        old: Record<string, unknown> | null;
      }) => {
        const row = payload.new ?? payload.old ?? {};
        const channelId =
          (row.channel_id as string | undefined) ??
          (row.id as string | undefined) ??
          null;
        const messageId =
          (row.message_id as string | undefined) ??
          (kind === 'message' ? (row.id as string | undefined) : undefined) ??
          null;
        onEvent({ kind, eventType: payload.eventType, channelId, messageId });
      },
    );
  };

  subscribe('channels', 'channel');
  subscribe('channel_members', 'member');
  subscribe('messages', 'message');
  subscribe('message_reactions', 'reaction');
  subscribe('mentions', 'mention');

  ch.subscribe();
  return ch;
}
